import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ShieldCheck } from 'lucide-react';
import { useSupportedCountries } from '../../hooks/useCountryConfig';
import { API_BASE_URL } from '../../services/api';

// Gulf calculators (GOSI / QPSA) are now legally certified — no country is gated.
const BETA_COUNTRY_CODES = [];

const API_ROOT = (API_BASE_URL || "").replace(/\/+$/, "");
const PROVIDER_LABELS = { google: 'Google', microsoft: 'Microsoft' };

// Register page lives on the root domain: OAuth starts on the main backend,
// and the callback redirects back to /register?social_email=… (see auth_social.py).
const buildOAuthUrl = (provider) => `${API_ROOT}/v1/auth/${provider}`;

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" fill="none">
    <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 5.1 29.6 3 24 3 12.9 3 4 11.9 4 23s8.9 20 20 20c11 0 19.7-7.9 19.7-20 0-1.3-.1-2.7-.2-4z" fill="#FFC107"/>
    <path d="M6.3 14.7l7 5.1C15.1 16 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 5.1 29.6 3 24 3 16.3 3 9.7 7.9 6.3 14.7z" fill="#FF3D00"/>
    <path d="M24 43c5.5 0 10.5-1.9 14.4-5.2l-6.6-5.6C29.6 34.1 26.9 35 24 35c-6.1 0-10.7-3.9-11.8-9.3l-7 5.4C8.7 38.7 15.8 43 24 43z" fill="#4CAF50"/>
    <path d="M44.5 20H24v8.5h11.8c-.6 2.5-2.1 4.7-4.2 6.2l6.6 5.6C42.1 36.6 44.5 30.2 44.5 20z" fill="#1976D2"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

const Step0CountrySelect = ({ formData, onNext, socialPrefill }) => {
  const { t, i18n } = useTranslation();
  const { countries, loading } = useSupportedCountries();
  const [selected, setSelected] = useState(formData?.country_code || null);
  const providerLabel = PROVIDER_LABELS[socialPrefill?.provider] || socialPrefill?.provider || '';

  const lang = i18n.language?.split('-')[0] || 'fr';
  const getCountryName = (country) => country[`name_${lang}`] || country.name_fr || country.name_en || country.name_ar || country.code;

  const displayCountries = countries.length > 0 ? countries : [
    { code: 'TN', name_fr: 'Tunisie',         name_en: 'Tunisia',      name_ar: 'تونس',             currency_code: 'TND', flag: '🇹🇳' },
    { code: 'QA', name_fr: 'Qatar',           name_en: 'Qatar',        name_ar: 'قطر',              currency_code: 'QAR', flag: '🇶🇦' },
    { code: 'MA', name_fr: 'Maroc',           name_en: 'Morocco',      name_ar: 'المغرب',           currency_code: 'MAD', flag: '🇲🇦' },
    { code: 'FR', name_fr: 'France',          name_en: 'France',       name_ar: 'فرنسا',            currency_code: 'EUR', flag: '🇫🇷' },
    { code: 'SA', name_fr: 'Arabie Saoudite', name_en: 'Saudi Arabia', name_ar: 'المملكة العربية السعودية', currency_code: 'SAR', flag: '🇸🇦' },
  ];

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4"
          style={{ backgroundColor: "rgba(94,106,210,0.15)", border: "1px solid rgba(94,106,210,0.3)" }}>
          <Globe className="w-5 h-5" style={{ color: "#5e6ad2" }} />
        </div>
        <h2 className="text-xl font-semibold mb-2" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>
          {t('registration.step0.title')}
        </h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
          {t('registration.step0.subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2" style={{ borderColor: "#5e6ad2" }} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mb-6">
          {displayCountries.map((country) => {
            const isBeta = BETA_COUNTRY_CODES.includes(country.code);
            return (
            <button
              key={country.code}
              type="button"
              onClick={() => !isBeta && setSelected(country.code)}
              disabled={isBeta}
              aria-pressed={selected === country.code}
              aria-label={`${getCountryName(country)} ${country.currency_code}`}
              className="relative flex flex-col items-center p-5 rounded-xl transition-all"
              style={{
                backgroundColor: isBeta ? "var(--lm-s2)" : selected === country.code ? "rgba(94,106,210,0.1)" : "var(--lm-s2)",
                border: `1px solid ${selected === country.code ? "#5e6ad2" : "var(--lm-border)"}`,
                opacity: isBeta ? 0.55 : 1,
                cursor: isBeta ? "not-allowed" : "pointer",
              }}
              onMouseEnter={e => { if (!isBeta && selected !== country.code) e.currentTarget.style.borderColor = "var(--lm-border-2)"; }}
              onMouseLeave={e => { if (!isBeta && selected !== country.code) e.currentTarget.style.borderColor = "var(--lm-border)"; }}
            >
              {selected === country.code && !isBeta && (
                <div className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#5e6ad2" }}>
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              )}
              {isBeta && (
                <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{ backgroundColor: "rgba(217,119,6,0.15)", color: "#b45309", fontSize: "10px" }}>
                  {t('common.comingSoon')}
                </div>
              )}
              <span className="text-3xl mb-2">{country.flag || '🌍'}</span>
              <span className="font-medium text-xs leading-snug text-center" style={{ color: "var(--lm-ink)" }}>{getCountryName(country)}</span>
              <span className="text-xs mt-0.5" style={{ color: "var(--lm-ink-3)" }}>{country.currency_code}</span>
            </button>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => onNext({ country_code: selected })}
        disabled={!selected || loading}
        className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors"
        style={{ backgroundColor: selected ? "#5e6ad2" : "var(--lm-s3)", color: selected ? "#ffffff" : "var(--lm-ink-3)", opacity: !selected ? 0.6 : 1, cursor: !selected ? "not-allowed" : "pointer" }}
        onMouseEnter={e => { if (selected) e.currentTarget.style.backgroundColor = "#828fff"; }}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = selected ? "#5e6ad2" : "var(--lm-s3)")}
      >
        {selected
          ? `${t('common.continueWith')} ${getCountryName(displayCountries.find(c => c.code === selected) || { code: selected })}`
          : t('common.selectCountryToContinue')}
      </button>

      {/* SSO signup — authenticate first, the wizard prefills and locks the admin email */}
      {socialPrefill ? (
        <div className="mt-6 px-4 py-3 rounded-lg flex items-center gap-3"
          style={{ backgroundColor: "rgba(94,106,210,0.08)", border: "1px solid rgba(94,106,210,0.25)" }}>
          <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{ color: "#828fff" }} />
          <p className="text-sm" style={{ color: "#828fff" }}>
            {t('registration.step0.socialConnected', { provider: providerLabel, email: socialPrefill.email })}
          </p>
        </div>
      ) : (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1" style={{ backgroundColor: "var(--lm-border)" }} />
            <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('registration.step0.orSignupWith')}</span>
            <div className="h-px flex-1" style={{ backgroundColor: "var(--lm-border)" }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { provider: 'google', label: 'Google', icon: <GoogleIcon /> },
              { provider: 'microsoft', label: 'Microsoft', icon: <MicrosoftIcon /> },
            ].map(({ provider, label, icon }) => (
              <button
                key={provider}
                type="button"
                onClick={() => { window.location.href = buildOAuthUrl(provider); }}
                className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: "var(--lm-canvas)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = "#5e6ad2")}
                onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--lm-border)")}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Step0CountrySelect;
